// src/contexts/ServerConnectionContext.js
import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import config from '../config/appConfig';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from './AuthContext';
import { playVoice } from '../utils/voicePlayer';
import {
    getServerInfoAppEndpoint,
    WS_SERVER_INFO_TOPICS,
    getServerInfoUserQueueTopic,
    WS_SERVER_INFO_TYPES
} from '../config/wsServerInfoConfig';

export const ServerConnectionContext = createContext({
    serverInfo: null,
    isConnectedToServer: false,
    isConnecting: false,
    connectionError: null,
    targetIp: null, // The IP we are currently targeting/connected to
    setTargetIp: (ip) => { },
    connectToServer: (ip) => { },
    disconnectFromServer: () => { },
});

export const useServerConnection = () => useContext(ServerConnectionContext);

export const ServerConnectionProvider = ({ children }) => {
    const [serverInfo, setServerInfo] = useState(null);
    const [isConnectedToServer, setIsConnectedToServer] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [targetIp, setTargetIp] = useState(null); // Track the target IP

    const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
    const { isAuthenticated, token } = useAuth();
    const subscriptions = useRef({ info: null, error: null });
    const connectionTimeoutRef = useRef(null);
    const connectionRequestIdRef = useRef(0);
    const isConnectedToServerRef = useRef(false);
    const serverInfoRef = useRef(null);
    const isConnectingRef = useRef(false);

    // Keep refs synchronized with state and auto-clear errors when connected
    useEffect(() => {
        isConnectedToServerRef.current = isConnectedToServer;
        serverInfoRef.current = serverInfo;
        isConnectingRef.current = isConnecting;
        if (serverInfo && isConnectedToServer) {
            setConnectionError(null);
        }
    }, [serverInfo, isConnectedToServer, isConnecting]);

    const clearConnectionTimeout = useCallback(() => {
        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
        }
    }, []);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => clearConnectionTimeout();
    }, [clearConnectionTimeout]);

    // --- Disconnect Logic ---
    const disconnectFromServer = useCallback((sendMessageToServer = true) => {
        console.log(`ServerConnectionContext: Disconnecting (Send WS: ${sendMessageToServer}). Current Target: ${targetIp}`);
        connectionRequestIdRef.current++;
        clearConnectionTimeout();
        if (sendMessageToServer && targetIp && isWebSocketConnected) {
            // Optional: Send disconnect message if your backend requires it
            // sendMessage(getServerInfoAppEndpoint(WS_SERVER_INFO_TOPICS.APP_DISCONNECT), { ip: targetIp });
        }
        setServerInfo(null);
        setIsConnectedToServer(false);
        setIsConnecting(false);
        setConnectionError(null);
        setTargetIp(null); // Clear target IP on disconnect
        serverInfoRef.current = null;
        isConnectedToServerRef.current = false;
        isConnectingRef.current = false;
    }, [targetIp, isWebSocketConnected, clearConnectionTimeout]);

    // --- WebSocket Handlers ---
    const handleServerInfoUpdate = useCallback((data, wsError) => {
        console.log("Server Info Received (Context):", data, wsError);
        connectionRequestIdRef.current++;
        clearConnectionTimeout();
        setIsConnecting(false);
        isConnectingRef.current = false;

        if (wsError || (data && data.error)) {
            const errorMsg = data?.error || wsError?.message || "Failed to connect/retrieve server info.";
            setConnectionError(errorMsg);
            setServerInfo(null);
            setIsConnectedToServer(false);
            serverInfoRef.current = null;
            isConnectedToServerRef.current = false;
            return;
        }

        if (data && data.online !== undefined) {
            setConnectionError(null);
            setServerInfo(data);
            serverInfoRef.current = data;
            playVoice('enter-server');
            if (data.online) {
                setIsConnectedToServer(true);
                isConnectedToServerRef.current = true;
            } else {
                setConnectionError("Target server is offline.");
                setIsConnectedToServer(false);
                isConnectedToServerRef.current = false;
            }
        } else {
            console.warn("Server Info Warning (Context): Invalid data structure", data);
            setConnectionError("Received invalid server data structure.");
            setServerInfo(null);
            setIsConnectedToServer(false);
            serverInfoRef.current = null;
            isConnectedToServerRef.current = false;
        }
    }, [clearConnectionTimeout]);

    const handleServerErrorUpdate = useCallback((errorData, wsError) => {
        console.error("Server General Error (Context):", errorData, wsError);
        connectionRequestIdRef.current++;
        clearConnectionTimeout();
        setIsConnecting(false);
        isConnectingRef.current = false;
        const errorMsg = errorData?.error || errorData?.message || wsError?.message || "Unknown connection error.";
        setConnectionError(errorMsg);
        setServerInfo(null);
        setIsConnectedToServer(false);
        serverInfoRef.current = null;
        isConnectedToServerRef.current = false;
    }, [clearConnectionTimeout]);

    // --- Connect Action ---
    const connectToServer = useCallback((ip) => {
        if (!ip || !ip.trim()) {
            setConnectionError("IP Address cannot be empty."); return;
        }
        const trimmedIp = ip.trim();
        setTargetIp(trimmedIp); // Set the target IP immediately so UI & inputs reflect it

        if (isConnectingRef.current) return; // Don't stack requests
        if (!isAuthenticated) { setConnectionError("Please log in first."); return; }
        if (!isWebSocketConnected) { setConnectionError("WebSocket not connected. Please wait or refresh."); return; }

        console.log(`ServerConnectionContext: Requesting connection to ${trimmedIp}`);

        clearConnectionTimeout();
        const requestId = ++connectionRequestIdRef.current;

        // Reset state before new connection attempt
        setServerInfo(null);
        setIsConnectedToServer(false);
        setConnectionError(null);
        setIsConnecting(true);
        isConnectingRef.current = true;
        serverInfoRef.current = null;
        isConnectedToServerRef.current = false;

        // 12-second safety timeout in case backend drops or never responds
        connectionTimeoutRef.current = setTimeout(() => {
            if (requestId !== connectionRequestIdRef.current) return;
            if (isConnectedToServerRef.current || serverInfoRef.current || !isConnectingRef.current) return;

            console.warn("Server connection timed out for:", trimmedIp);
            setIsConnecting(false);
            isConnectingRef.current = false;
            setConnectionError("Превышено время ожидания ответа от сервера. Попробуйте еще раз.");
        }, 12000);

        // Send connection request via WebSocket
        sendMessage(getServerInfoAppEndpoint(WS_SERVER_INFO_TOPICS.APP_CONNECT), { ip: trimmedIp });

    }, [isAuthenticated, isWebSocketConnected, sendMessage, clearConnectionTimeout]);

    // --- Subscription Effect ---
    useEffect(() => {
        if (!isWebSocketConnected || !isAuthenticated) {
            disconnectFromServer(false);
            subscriptions.current.info?.();
            subscriptions.current.error?.();
            subscriptions.current = { info: null, error: null };
            return;
        }

        console.log("ServerConnectionContext: [EFFECT] Subscribing to server info topics...");

        // Unsubscribe from previous if they exist
        subscriptions.current.info?.();
        subscriptions.current.error?.();

        // Subscribe to user-specific info and error topics
        const infoTopic = getServerInfoUserQueueTopic(WS_SERVER_INFO_TOPICS.USER_INFO);
        const errorTopic = getServerInfoUserQueueTopic(WS_SERVER_INFO_TOPICS.USER_ERROR);

        subscriptions.current.info = subscribe(infoTopic, handleServerInfoUpdate);
        subscriptions.current.error = subscribe(errorTopic, handleServerErrorUpdate);

        return () => {
            console.log("ServerConnectionContext: [CLEANUP] Unsubscribing from topics...");
            subscriptions.current.info?.();
            subscriptions.current.error?.();
            subscriptions.current = { info: null, error: null };
        };
        // Removed disconnectFromServer and other potential unstable dependencies.
        // handleServerInfoUpdate/handleServerErrorUpdate are stable (empty dep array).
    }, [isWebSocketConnected, isAuthenticated, subscribe, handleServerInfoUpdate, handleServerErrorUpdate]);

    // --- Add effect to handle logout/auth changes explicitly ---
    const prevAuthRef = useRef(isAuthenticated);
    useEffect(() => {
        if (prevAuthRef.current && !isAuthenticated) {
            console.log("ServerConnectionContext: Auth lost, ensuring disconnect.");
            disconnectFromServer(false); // Reset state on logout
        }
        prevAuthRef.current = isAuthenticated;
    }, [isAuthenticated, disconnectFromServer]);


    return (
        <ServerConnectionContext.Provider value={{
            serverInfo,
            isConnectedToServer,
            isConnecting,
            connectionError,
            targetIp, // Expose the target IP
            setTargetIp,
            connectToServer,
            disconnectFromServer,
        }}>
            {children}
        </ServerConnectionContext.Provider>
    );
};